import type { InspectionTemplateColumn } from "@/shared/models/Inspection";
import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import type {
	ExcelCell,
	ExcelExportParams,
	ExcelRow,
	ExcelStyle,
} from "ag-grid-community";
import {
	InspectionReportRow,
	InspectionReportSheetDetail,
} from "../../models/InspectionReport";
import { formatCellValue, readCellValue } from "../../models/Reading";

const moment = getMomentTz();

const SHEET_HEADER = "xl-sheet-header";
const SHEET_CELL = "xl-sheet-cell";
const DETAIL_GROUP = "xl-detail-group";
const DETAIL_HEADER = "xl-detail-header";
const DETAIL_CELL = "xl-detail-cell";
const DETAIL_NOGO = "xl-detail-nogo";
const DETAIL_DEFECTS = "xl-detail-defects";
const DETAIL_NOTE = "xl-detail-note";

export const EXPORT_COLUMNS = [
	"SectionName",
	"Rev",
	"EventId",
	"ProductionDate",
	"InspectorName",
	"ReadingCount",
	"NoGoCount",
	"DefectCount",
	"Comments",
];

const COLUMN_WIDTH = 78;

const SHEET_FIELDS: {
	label: string;
	span: number;
	value: (sheet: InspectionReportRow) => string;
}[] = [
	{ label: "Section", span: 1, value: (sheet) => sheet.SectionName ?? "" },
	{ label: "Rev", span: 1, value: (sheet) => `Rev ${sheet.Rev}` },
	{
		label: "Event",
		span: 1,
		value: (sheet) => (sheet.EventId ? `#${sheet.EventId}` : "-"),
	},
	{
		label: "Shift",
		span: 2,
		value: (sheet) => {
			if (!sheet.ProductionDate) return "";

			const shift = `${sheet.Shift ?? ""}${sheet.Crew ? `-${sheet.Crew}` : ""}`;

			return `${moment.utc(sheet.ProductionDate).format("MM/DD/YY")} ${shift}`.trim();
		},
	},
	{
		label: "Inspector",
		span: 2,
		value: (sheet) => sheet.InspectorName ?? "-",
	},
	{
		label: "Readings",
		span: 1,
		value: (sheet) =>
			sheet.ReadingCount < sheet.SheetReadingCount
				? `${sheet.ReadingCount} of ${sheet.SheetReadingCount}`
				: `${sheet.ReadingCount}`,
	},
	{ label: "No-Go", span: 1, value: (sheet) => `${sheet.NoGoCount}` },
	{ label: "Defects", span: 1, value: (sheet) => `${sheet.DefectCount}` },
	{ label: "Comments", span: 0, value: (sheet) => sheet.Comments ?? "-" },
];

const FIXED_SPAN = SHEET_FIELDS.reduce((total, field) => total + field.span, 0);

const DETAIL_INDENT = 1;
const DETAIL_LEAD = 3; // #, Time, Heat
const MIN_DEFECTS_SPAN = 2;

const BORDER = {
	lineStyle: "Continuous",
	weight: 0,
	color: "#d0d7de",
} as const;

const ALL_BORDERS = {
	borderTop: BORDER,
	borderBottom: BORDER,
	borderLeft: BORDER,
	borderRight: BORDER,
};

export const INSPECTION_EXCEL_STYLES: ExcelStyle[] = [
	{
		id: SHEET_HEADER,
		font: { bold: true, color: "#FFFFFF", size: 11 },
		interior: { color: "#37474F", pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: SHEET_CELL,
		font: { size: 11, bold: true, color: "#263238" },
		alignment: { horizontal: "Left", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: DETAIL_GROUP,
		font: { bold: true, color: "#37474F", size: 10 },
		interior: { color: "#CFD8DC", pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: DETAIL_HEADER,
		font: { bold: true, color: "#37474F", size: 10 },
		interior: { color: "#ECEFF1", pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: DETAIL_CELL,
		font: { size: 10 },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: DETAIL_NOGO,
		font: { size: 10, bold: true, color: "#C62828" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: DETAIL_DEFECTS,
		font: { size: 10, color: "#E65100" },
		alignment: { horizontal: "Left", vertical: "Center" },
		borders: ALL_BORDERS,
	},
	{
		id: DETAIL_NOTE,
		font: { size: 10, italic: true, color: "#78909C" },
		alignment: { horizontal: "Left", vertical: "Center" },
	},
];

const cell = (value: string, styleId: string, span = 1): ExcelCell => ({
	styleId,
	...(span > 1 ? { mergeAcross: span - 1 } : {}),
	data: { type: "String", value },
});

const INDENT: ExcelCell = { data: { type: "String", value: "" } };

const columnGroups = (columns: InspectionTemplateColumn[]) =>
	columns.reduce<{ name: string; span: number }[]>((runs, column) => {
		const name = column.GroupName ?? "";
		const last = runs[runs.length - 1];

		if (last && last.name === name && name !== "") last.span += 1;
		else runs.push({ name, span: 1 });

		return runs;
	}, []);

const sheetRow = (
	cells: (field: (typeof SHEET_FIELDS)[number]) => ExcelCell,
	commentsSpan: number,
): ExcelRow => ({
	cells: SHEET_FIELDS.map((field) =>
		cells(field.span === 0 ? { ...field, span: commentsSpan } : field),
	),
});

const readingRows = (
	detail: InspectionReportSheetDetail | undefined,
	totalColumns: number,
): ExcelRow[] => {
	const columns = detail?.columns ?? [];
	const readings = detail?.readings ?? [];

	const defectsSpan = Math.max(
		totalColumns - DETAIL_INDENT - DETAIL_LEAD - columns.length,
		1,
	);

	if (readings.length === 0)
		return [
			{ cells: [INDENT, cell("Nothing was read on this sheet.", DETAIL_NOTE)] },
			{ cells: [] },
		];

	const groups: ExcelRow = {
		cells: [
			INDENT,
			...["", "", ""].map((label) => cell(label, DETAIL_HEADER)),
			...columnGroups(columns).map((group) =>
				cell(group.name, group.name ? DETAIL_GROUP : DETAIL_HEADER, group.span),
			),
			cell("", DETAIL_HEADER, defectsSpan),
		],
	};

	const header: ExcelRow = {
		cells: [
			INDENT,
			...[
				"#",
				"Time",
				"Heat",
				...columns.map((column) => column.ColumnLabel),
			].map((label) => cell(label, DETAIL_HEADER)),
			cell("Surface defects", DETAIL_HEADER, defectsSpan),
		],
	};

	const rows = readings.map(
		(reading, index): ExcelRow => ({
			cells: [
				INDENT,
				cell(`${index + 1}`, DETAIL_CELL),
				cell(
					dxtToLocalServerTime(reading.RowTimestamp, "MM/dd HH:mm"),
					DETAIL_CELL,
				),
				cell(reading.HeatName ?? "", DETAIL_CELL),
				...columns.map((column) => {
					const value = reading.ValueByColumnId[column.ColumnId as number];

					return cell(
						formatCellValue(column, readCellValue(column, value)),
						value?.PassFail === false ? DETAIL_NOGO : DETAIL_CELL,
					);
				}),
				cell(
					reading.Defects.map((defect) => defect.SurfaceDefectName ?? "?").join(
						", ",
					),
					DETAIL_DEFECTS,
					defectsSpan,
				),
			],
		}),
	);

	return [groups, header, ...rows, { cells: [] }];
};

export const inspectionsExcelParams = (
	sheets: InspectionReportRow[],
	details: Map<number, InspectionReportSheetDetail> | undefined,
): ExcelExportParams => {
	const totalColumns = sheets.reduce((widest, sheet) => {
		const columns = details?.get(sheet.InspectionId)?.columns.length ?? 0;

		return Math.max(
			widest,
			DETAIL_INDENT + DETAIL_LEAD + columns + MIN_DEFECTS_SPAN,
		);
	}, FIXED_SPAN + 1);

	const commentsSpan = totalColumns - FIXED_SPAN;

	const rows: ExcelRow[] = [
		sheetRow(
			(field) => cell(field.label, SHEET_HEADER, field.span),
			commentsSpan,
		),
		...sheets.flatMap((sheet) => [
			sheetRow(
				(field) => cell(field.value(sheet), SHEET_CELL, field.span),
				commentsSpan,
			),
			...readingRows(details?.get(sheet.InspectionId), totalColumns),
		]),
	];

	return {
		fileName: `inspections_report_${dxtToLocalServerTime(new Date(), "yyyyMMdd_HHmm")}.xlsx`,
		sheetName: "Inspections",
		columnKeys: EXPORT_COLUMNS,
		shouldRowBeSkipped: () => true,
		processHeaderCallback: () => "",
		freezeRows: "headers",
		columnWidth: () => COLUMN_WIDTH,
		rowHeight: 18,
		prependContent: rows,
	};
};
